import { fetchLeads } from "../slices/leads/lead.slice";
import { fetchDepartments } from "../slices/departments/department.slice";
import { fetchCountries } from "../slices/countries/country.slice";
import { fetchClients } from "../slices/clients/client.slice";
import { fetchClientContacts } from "../slices/clientContacts/clientContact.slice";
import { fetchClientSites } from "../slices/clientSites/clientSite.slice";
// NOTE: fetchProfiles removed - profiles are loaded on-demand:
// - Super Admin: ProfileList.tsx
// - Tenant: React Query hooks (useTenantProfilesOptions)
// - Client: React Query hooks (useClientProfilesOptions)
import { fetchRoles } from "../slices/roles/role.slice";
import { fetchClientRentalConfigs } from "../slices/clientRentalConfig/clientRentalConfig.slice";
import { DEFAULT_PAGE_SIZE, getPageSize } from "../common/constants";

/**
 * Centralized App Initialization Service
 * Handles loading of master data once on application bootstrap
 */
class AppInitService {
    private isInitialized = false;
    private isLoading = false;

    /**
     * Initialize application data on app startup
     * Loads all required master data once
     */
    async initialize(dispatch: any) {
        if (this.isInitialized || this.isLoading) {
            if (process.env.NODE_ENV === 'development') {
                console.log("⚠️ AppInitService.initialize() called but already initialized or loading. Caller:", new Error().stack);
            }
            return;
        }

        this.isLoading = true;
        try {
            if (process.env.NODE_ENV === 'development') {
                console.log("🟢 AppInitService.initialize() called. Stack:", new Error().stack);
            }
            console.log("Initializing app data...");

            // --- Load MASTER DATA (Configurations, Dropdowns) ---
            
            // Load Countries (Static)
            await dispatch(fetchCountries({ pageNumber: 1, pageSize: getPageSize(50) }));

            // Load Roles (Static/Config)
            await dispatch(fetchRoles({ pageNumber: 1, pageSize: getPageSize(50) }));

            // Load Departments (Config) - use DEFAULT_PAGE_SIZE from config
            await dispatch(fetchDepartments({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE }));

            // NOTE: Profiles are NOT loaded during initialization
            // - Super Admin profiles: NOT USED (no ProfileList page)
            // - Tenant profiles: Loaded via React Query in TenantRoleCreate.tsx (uses /api/v1/Profile/Tenant)
            // - Client profiles: Loaded via React Query when needed (uses /api/v1/Profile/Client)

            // --- BUSINESS DATA (Loaded Lazily on Pages) ---
            // Commented out to optimize startup performance. 
            // Pages (LeadList, ClientList, etc.) now fetch their own data on mount.

            // await dispatch(fetchLeads({ pageNumber: 1, pageSize: 500 }));
            // await dispatch(fetchClients({ pageNumber: 1, pageSize: 50 }));
            // await dispatch(fetchClientContacts({ pageNumber: 1, pageSize: 50 }));
            // await dispatch(fetchClientSites({ pageNumber: 1, pageSize: 50 }));
            // await dispatch(fetchClientRentalConfigs({ pageNumber: 1, pageSize: 50 }));

            // Future: Load other master data here
            // await dispatch(fetchMeetings());

            this.isInitialized = true;
            console.log("App initialization complete");
        } catch (error) {
            console.error("App initialization failed:", error);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Force refresh leads data
     * Used after CRUD operations
     */
    async forceRefreshLeads(dispatch: any) {
        await dispatch(fetchLeads({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE }));
    }

    /**
     * Force refresh departments data
     * Used after CRUD operations
     */
    async forceRefreshDepartments(dispatch: any) {
        await dispatch(fetchDepartments({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE }));
    }

    /**
     * Force refresh countries data
     * Used after CRUD operations or manual refresh
     */
    async forceRefreshCountries(dispatch: any) {
        await dispatch(fetchCountries({ pageNumber: 1, pageSize: getPageSize(50) }));
    }

    /**
     * Force refresh clients data
     * Used after CRUD operations
     */
    async forceRefreshClients(dispatch: any) {
        await dispatch(fetchClients({ pageNumber: 1, pageSize: getPageSize(50) }));
    }

    /**
     * Force refresh client contacts data
     * Used after CRUD operations
     */
    async forceRefreshClientContacts(dispatch: any) {
        await dispatch(fetchClientContacts({ pageNumber: 1, pageSize: getPageSize(50) }));
    }

    /**
     * Force refresh client sites data
     * Used after CRUD operations
     */
    async forceRefreshClientSites(dispatch: any) {
        await dispatch(fetchClientSites({ pageNumber: 1, pageSize: getPageSize(50) }));
    }

    /**
     * Force refresh client rental configs data
     * Used after CRUD operations
     */
    async forceRefreshClientRentalConfigs(dispatch: any) {
        await dispatch(fetchClientRentalConfigs({ pageNumber: 1, pageSize: 50 }));
    }

    /**
     * Force refresh user profiles data
     * NOTE: DEPRECATED - Super Admin profiles page will never be implemented
     * Tenant/Client profiles use React Query and invalidate queries instead
     * This method is kept for backward compatibility but should not be used
     */
    async forceRefreshProfiles(dispatch: any) {
        // NOTE: Super Admin profiles are not used - this method is deprecated
        console.warn("forceRefreshProfiles is deprecated - Super Admin profiles page will never be implemented");
        // Import here to avoid circular dependency
        // const { fetchProfiles } = await import("../slices/userProfiles/profile.slice");
        // await dispatch(fetchProfiles({ pageNumber: 1, pageSize: 50 }));
    }

    /**
     * Force refresh roles data
     * Used after CRUD operations
     */
    async forceRefreshRoles(dispatch: any) {
        await dispatch(fetchRoles({ pageNumber: 1, pageSize: 20 }));
    }

    /**
     * Reset initialization state
     * Useful for logout or re-initialization scenarios
     */
    reset() {
        this.isInitialized = false;
        this.isLoading = false;
    }

    /**
     * Check if app is initialized
     */
    getIsInitialized(): boolean {
        return this.isInitialized;
    }
}

// Export singleton instance
export const appInitService = new AppInitService();
